import { STANDARD_GRAVITY, cross, dot, scale, type Vec3 } from '@rcs/domain';
import type { TrackPoint } from './track-lookup';
import type { Frame, GForce } from './types';

/** 点の姿勢（前方 T・上方 U・右方 R = T × U）。 */
export function frameAt(point: TrackPoint): Frame {
  return { tangent: point.tangent, up: point.up, right: cross(point.tangent, point.up) };
}

/**
 * 乗客が感じる G を求める（docs/physics/physics-model.md 6 章）。
 *
 * 比力 `f = a − g⃗`（a は加速度、g⃗ は重力加速度ベクトル）を標準重力加速度 g₀ で割り、姿勢へ分解する。
 *
 * @param point 軌道上の点
 * @param speed 弧長方向の速度 [m/s]
 * @param tangentialAcceleration 接線加速度 dv/dt [m/s²]
 * @param gravity 環境の重力加速度の大きさ [m/s²]
 */
export function computeGForce(
  point: TrackPoint,
  speed: number,
  tangentialAcceleration: number,
  gravity: number,
): GForce {
  const gravityVector: Vec3 = [0, -gravity, 0];
  const acceleration = [
    tangentialAcceleration * point.tangent[0] + speed * speed * point.curvature[0],
    tangentialAcceleration * point.tangent[1] + speed * speed * point.curvature[1],
    tangentialAcceleration * point.tangent[2] + speed * speed * point.curvature[2],
  ] as const;
  const specificForce = scale(
    [
      acceleration[0] - gravityVector[0],
      acceleration[1] - gravityVector[1],
      acceleration[2] - gravityVector[2],
    ],
    1 / STANDARD_GRAVITY,
  );
  const frame = frameAt(point);
  return {
    vertical: dot(specificForce, frame.up),
    lateral: dot(specificForce, frame.right),
    longitudinal: dot(specificForce, frame.tangent),
  };
}
